import React from "react";

import _ from "lodash";
import styled from "styled-components";

import ParentGatewayInfo from "./components/ParentGatewayInfo";
import VaspCardSkeleton from "./components/VaspCardSkeleton";
import {
  VaspCardContainer,
  MainContentContainer,
  VASPHeaderLogo,
  VASPInfoContainer,
  Title,
  StatusContainer,
  Subtitle,
  CallToAction,
  LogoAndInfoContainer,
  JurisdictionItemsContainer,
} from "./styles";

import Tooltip from "~/components/Tooltip";
import getCountry from "~/data/getCountry";
import VASPStatus from "~/templates/companies/components/VASPStatus";
import VaspDueDiligenceReviewSection from "~/templates/topCounterpartyVASPs/components/VaspDueDiligenceReviewSection/VaspDueDiligenceReviewSection";
import { VASP } from "~/types/vaspid";

interface Props {
  vasp?: VASP;
  onClick: () => void;
  actionButton?: React.ReactNode; //the parent can provide an actionButton
  isSelected?: boolean;
  parentCustomSubtitle?: string;
  includeGatewayBelonging?: boolean;
  includeVaspReview?: boolean;
}

export default function VaspCard({
  vasp,
  onClick,
  isSelected = false,
  actionButton,
  parentCustomSubtitle,
  includeGatewayBelonging,
  includeVaspReview = false,
}: Props) {
  if (!vasp) {
    //Return The skeleton placeholder if the vasp is not loaded yet
    return <VaspCardSkeleton />;
  }

  const isParent = vasp?.subsidiaries && vasp.subsidiaries?.length > 0;

  const subtitle = isParent
    ? parentCustomSubtitle || `${vasp.subsidiaries?.length} Jurisdictions`
    : _.join(
        _.compact([
          getCountry(vasp.jurisdictions),
          vasp.division ? getCountry(vasp.jurisdictions, vasp.division) : null,
        ]),
        " - ",
      );

  return (
    <VaspCardContainer
      hasActionButton={Boolean(actionButton)}
      hasUpperContent={includeGatewayBelonging}
      isSelected={isSelected}
      onClick={onClick}
    >
      {includeGatewayBelonging && <ParentGatewayInfo vasp={vasp} />}
      <MainContentContainer>
        <div data-testid="vasp-card" style={{ width: "80%" }}>
          <LogoAndInfoContainer hasActionButton={Boolean(actionButton)}>
            <VASPHeaderLogo
              src={vasp.logo ? vasp.logo : "/assets/vasp-fallback.png"}
              jurisdictions={vasp.jurisdictions}
              className="localize-ignore"
            />
            <VASPInfoContainer hasActionButton={Boolean(actionButton)}>
              <Title className="localize-ignore">{vasp.name}</Title>
              {subtitle && <Subtitle>{subtitle}</Subtitle>}
            </VASPInfoContainer>
          </LogoAndInfoContainer>
          <StatusContainer>
            {!isParent ? (
              <VASPStatus
                isVerified={vasp.verificationStatus === "VERIFIED"}
                isClaimed={!!vasp.hasAdmin}
                isInNetwork={!!vasp.isNotifiable}
              />
            ) : (
              <JurisdictionItemsContainer>
                {vasp.subsidiaries?.map((sub) => (
                  <Tooltip
                    id={"tooltip-jurisdiction" + sub.name}
                    HoverComponent={({ ...props }) => (
                      <FlagIcon
                        {...props}
                        key={sub.jurisdictions}
                        className={`fi fi-${sub.jurisdictions?.toLowerCase()}`}
                      />
                    )}
                    key={sub.did}
                    customContainerStyles={{ maxWidth: "unset" }}
                  >
                    <div>
                      <p>{`${sub.name} ${getCountry(sub.jurisdictions)}`}</p>
                      {sub.hasAdmin && <p>🚩&nbsp;Claimed</p>}
                      {sub.verificationStatus === "VERIFIED" && <p>✅&nbsp;Verified</p>}
                    </div>
                  </Tooltip>
                ))}
              </JurisdictionItemsContainer>
            )}
          </StatusContainer>
        </div>
        <CallToAction>{actionButton}</CallToAction>
        {includeVaspReview && (
          <VaspDueDiligenceReviewSection
            vaspDID={vasp.did}
            vaspReviewStatus={vasp.reviewValue}
            reviewedDate={vasp.reviewDate ? new Date(vasp.reviewDate) : null}
          />
        )}
      </MainContentContainer>
    </VaspCardContainer>
  );
}

const FlagIcon = styled.span`
  margin-right: 16px;
  width: 24px;
  height: 24px;
  border-radius: 100%;
  background-size: cover;
  border: 1px solid #d2d2d2;
`;
